// Pure, dependency-free aggregation logic for the instructor dashboard's "needing
// attention" summary. No React, no Supabase client — the dashboard hook fetches the
// authorized rows (already RLS-scoped to the calling instructor) and passes them here.
// Kept framework-free so the exact same logic is reusable by a future React Native /
// Expo instructor app without any change.

export type SkillOutcome = 'demonstrated' | 'needs_practice' | 'not_assessed' | 'taught'

export interface AssessmentRow {
  studentId: string
  skillId: string
  outcome: SkillOutcome
  updatedAt: string
}

/**
 * One row per (lesson, skill) assessment EVENT can exist for many lessons over time —
 * re-assessing the same skill in a later lesson creates a new row (migration 057's own
 * design; see that migration's header). "Currently needing practice" must reflect only
 * the LATEST assessment per (student, skill) pair, exactly the same definition already
 * used by useStudentSkillProgress for one student — this just applies it across all of
 * the instructor's students at once, so the two can never silently disagree.
 *
 * Returns a Map of studentId -> count of skills whose latest recorded outcome is
 * 'needs_practice'. A skill never assessed, or whose latest outcome is 'demonstrated' or
 * 'not_assessed', does not count.
 */
export function countNeedsPracticeByStudent(rows: AssessmentRow[]): Map<string, number> {
  const latestByStudentSkill = new Map<string, AssessmentRow>()
  for (const row of rows) {
    const key = `${row.studentId}:${row.skillId}`
    const existing = latestByStudentSkill.get(key)
    if (!existing || new Date(row.updatedAt) > new Date(existing.updatedAt)) {
      latestByStudentSkill.set(key, row)
    }
  }

  const counts = new Map<string, number>()
  for (const row of latestByStudentSkill.values()) {
    if (row.outcome === 'needs_practice') {
      counts.set(row.studentId, (counts.get(row.studentId) ?? 0) + 1)
    }
  }
  return counts
}

export type G1AttentionReason = 'g1_expired' | 'g1_expiring_soon'

/**
 * Same 90-day "urgent" threshold already used by Students.tsx/StudentDetail.tsx's own
 * g1ExpiryLabel/g1ExpiryStatus — reusing the existing, already-established rule rather
 * than inventing a new one. A student with no recorded G1 expiry date is not flagged —
 * there is nothing to act on, the same convention those components already follow.
 */
export function g1AttentionReason(expiryIso: string | null, now: Date): G1AttentionReason | null {
  if (!expiryIso) return null
  const expiry = new Date(expiryIso)
  const days = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  if (days < 0) return 'g1_expired'
  if (days <= 90) return 'g1_expiring_soon'
  return null
}

export interface AttentionStudent {
  studentId: string
  fullName: string | null
  email: string
  needsPracticeCount: number
  g1Reason: G1AttentionReason | null
}

export interface ActiveStudentInput {
  studentId: string
  fullName: string | null
  email: string
  g1ExpiryDate: string | null
}

/**
 * Combines the two independently-defensible, already-established rules above into one
 * "needing attention" list — a student appears if EITHER rule applies, never because of
 * an invented score or percentage. Only ACTIVE students are considered for the G1 check
 * (nothing to act on for a former student); needs-practice counts are computed across
 * every assessment this instructor has ever recorded, but only surfaced here for
 * students currently in the active list, for the same reason.
 */
export function buildNeedsAttentionList(
  activeStudents: ActiveStudentInput[],
  assessmentRows: AssessmentRow[],
  now: Date,
): AttentionStudent[] {
  const needsPracticeCounts = countNeedsPracticeByStudent(assessmentRows)

  const result: AttentionStudent[] = []
  for (const student of activeStudents) {
    const g1Reason = g1AttentionReason(student.g1ExpiryDate, now)
    const needsPracticeCount = needsPracticeCounts.get(student.studentId) ?? 0
    if (g1Reason !== null || needsPracticeCount > 0) {
      result.push({
        studentId: student.studentId,
        fullName: student.fullName,
        email: student.email,
        needsPracticeCount,
        g1Reason,
      })
    }
  }
  return result
}
